import { type ReactNode, useMemo, useState } from "react"
import { ChevronRight } from "lucide-react"

import { Icon } from "components/icons/Icon"
import { CohortAvatar, isCohortEventLive } from "components/mentorship/CohortExperience"
import type { EventsListItem } from "components/mentorship/EventsListCard"
import { Button } from "components/ui/button"
import { meetingDateKey, meetingTimeZoneLabel, resolveMeetingTimeZone } from "utils/meetingTime"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "components/ui/sheet"

export type CohortRelationshipPerson = {
  id: string | number
  name: string
  initials: string
  avatarUrl?: string | null
  email?: string | null
}

type RelationshipMeeting = EventsListItem & {
  startTimeIso?: string
  endTimeIso?: string
}

type CohortRelationshipSpotlightProps = {
  viewerTimeZone?: string
  display?: "responsive" | "mobile" | "desktop"
  viewerIsMentor: boolean
  cohortId?: string
  currentUserId?: string
  mentor?: CohortRelationshipPerson | null
  mentees?: CohortRelationshipPerson[]
  events: RelationshipMeeting[]
  mentorLabel?: string
  menteeLabelPlural?: string
  mobileTrigger?: ReactNode
  onScheduleTime?: (person: CohortRelationshipPerson) => void
  onJoinMeeting?: (event: EventsListItem) => void
  onOpenPeople?: () => void
  onReportConcern?: (person: CohortRelationshipPerson) => void
  onViewProfile?: (person: CohortRelationshipPerson) => void
  continuityCues?: Record<string, string>
  onOpenContinuity?: (person: CohortRelationshipPerson) => void
}

export const relationshipEventsFor = (
  events: RelationshipMeeting[],
  cohortId: string | undefined,
  currentUserId: string | undefined,
  personId: string | number,
) => {
  if (!cohortId || !currentUserId) return []
  const targetId = String(personId)
  const pairIds = new Set([currentUserId, targetId])

  return events.filter((event) => {
    if (!event.cohortId || String(event.cohortId) !== cohortId) return false
    if (event.inviteesMode !== "custom") return false
    const hostId = event.createdById ? String(event.createdById) : null
    const invitedIds = new Set((event.invitedUserIds || []).map(String))

    // A cohort administrator may be the technical host while scheduling for the pair.
    // Exclude the host from the invitee audience, then require exactly the assigned pair
    // members who are not already represented by the host.
    if (hostId) invitedIds.delete(hostId)
    const requiredInvitees = [...pairIds].filter((id) => id !== hostId)
    return requiredInvitees.every((id) => invitedIds.has(id)) &&
      [...invitedIds].every((id) => pairIds.has(id))
  })
}

const formatMeetingDateTime = (event: RelationshipMeeting, compact = false, viewerTimeZone?: string) => {
  const start = event.startTimeIso ? new Date(event.startTimeIso) : null
  const end = event.endTimeIso ? new Date(event.endTimeIso) : null
  if (!start || Number.isNaN(start.getTime())) return event.date || "Time to be confirmed"

  const timeZone = resolveMeetingTimeZone(event.timeZone, viewerTimeZone)
  const formatTime = (date: Date) => date.toLocaleTimeString("en-US", { timeZone, hour: "numeric", minute: "2-digit" })
  const todayKey = meetingDateKey(new Date(), timeZone)
  const [year, month, day] = todayKey.split('-').map(Number)
  const tomorrowKey = new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10)
  const startKey = meetingDateKey(start, timeZone)
  const dateLabel = startKey === todayKey
    ? "today"
    : startKey === tomorrowKey
      ? "tomorrow"
      : start.toLocaleDateString("en-US", { timeZone, ...(compact
        ? { month: "short", day: "numeric" }
        : { weekday: "short", month: "short", day: "numeric" }) } as Intl.DateTimeFormatOptions)
  const startTime = formatTime(start)

  const zoneLabel = meetingTimeZoneLabel(start, timeZone)
  if (compact) return `${dateLabel}, ${startTime} ${zoneLabel}`
  const capitalizedDate = dateLabel[0].toUpperCase() + dateLabel.slice(1)
  const endTime = end && !Number.isNaN(end.getTime()) ? ` – ${formatTime(end)}` : ""
  return `${capitalizedDate}, ${startTime}${endTime} ${zoneLabel}`
}

const meetingPlace = (event: RelationshipMeeting) =>
  event.isVirtual ? "Online" : event.location || "In person"

const calendarHref = (event: RelationshipMeeting) => {
  const start = event.startTimeIso ? new Date(event.startTimeIso) : null
  const end = event.endTimeIso ? new Date(event.endTimeIso) : null
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  const calendarDate = (date: Date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${calendarDate(start)}/${calendarDate(end)}`,
    details: event.description || "",
    location: event.isVirtual ? "Online" : event.location || "",
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

const mailtoHref = (person: CohortRelationshipPerson, subject?: string) => {
  if (!person.email) return null
  const params = subject ? `?subject=${encodeURIComponent(subject)}` : ""
  return `mailto:${person.email}${params}`
}

export const getRelationshipMeetings = (
  events: RelationshipMeeting[],
  cohortId: string | undefined,
  currentUserId: string | undefined,
  personId: string | number,
) => {
  const now = Date.now()
  const matching = relationshipEventsFor(events, cohortId, currentUserId, personId)
  const next = [...matching]
    .filter((event) => {
      const end = event.endTimeIso ? new Date(event.endTimeIso).getTime() : Number.POSITIVE_INFINITY
      return event.status !== "past" && end >= now
    })
    .sort((first, second) => {
      if (isCohortEventLive(first) !== isCohortEventLive(second)) return isCohortEventLive(first) ? -1 : 1
      return new Date(first.startTimeIso || 0).getTime() - new Date(second.startTimeIso || 0).getTime()
    })[0] ?? null
  const last = [...matching]
    .filter((event) => event.status === "past" || (event.endTimeIso && new Date(event.endTimeIso).getTime() < now))
    .sort((first, second) => new Date(second.startTimeIso || 0).getTime() - new Date(first.startTimeIso || 0).getTime())[0] ?? null
  return { next, last }
}

function MentorIdentity({ mentor, eyebrow }: { mentor: CohortRelationshipPerson; eyebrow: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3.5 text-left">
      <CohortAvatar person={mentor} size="relationship" tone="mentor" />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-[0.06em] text-[var(--cohort-ink-muted)]">{eyebrow}</p>
        <p className="mt-[3px] break-words font-['Neue_Montreal','Inter',sans-serif] text-[21px] leading-tight tracking-[-0.01em] text-[var(--cohort-ink)]">{mentor.name}</p>
      </div>
    </div>
  )
}

function MentorActions({
  mentor,
  onViewProfile,
}: {
  mentor: CohortRelationshipPerson
  onViewProfile?: (person: CohortRelationshipPerson) => void
}) {
  const messageHref = mailtoHref(mentor)
  const scheduleRequestHref = mailtoHref(mentor, "Schedule a 1:1")
  const actionClass = "inline-flex min-h-12 w-full items-center justify-center rounded-xl px-4 text-base font-semibold"
  const secondaryClass = "cohort-secondary-button inline-flex min-h-12 w-full items-center justify-center rounded-xl px-4 text-base"

  const messageAction = messageHref ? (
    <a href={messageHref} className={`${actionClass} cohort-primary-button`}>Message {mentor.name.split(" ")[0]}</a>
  ) : (
    <button type="button" onClick={() => onViewProfile?.(mentor)} className={`${actionClass} cohort-primary-button`}>View profile</button>
  )

  return <>{messageAction}{scheduleRequestHref ? <a href={scheduleRequestHref} className={secondaryClass}>Ask to schedule</a> : null}</>
}

function MentorPanel({
  viewerTimeZone,
  mentor,
  nextMeeting,
  lastMeeting,
  onJoinMeeting,
  onReportConcern,
  onViewProfile,
  continuityCue,
  onOpenContinuity,
}: {
  viewerTimeZone?: string
  mentor: CohortRelationshipPerson
  nextMeeting: RelationshipMeeting | null
  lastMeeting: RelationshipMeeting | null
  onJoinMeeting?: (event: EventsListItem) => void
  onReportConcern?: (person: CohortRelationshipPerson) => void
  onViewProfile?: (person: CohortRelationshipPerson) => void
  continuityCue?: string
  onOpenContinuity?: (person: CohortRelationshipPerson) => void
}) {
  const live = Boolean(nextMeeting && isCohortEventLive(nextMeeting))
  const canJoin = Boolean(live && nextMeeting?.canJoinVideo && onJoinMeeting)
  const addToCalendarHref = nextMeeting ? calendarHref(nextMeeting) : null

  return (
    <>
      <div className="flex items-center gap-3.5 px-5 pb-[18px] pt-3.5 lg:pt-5">
        <CohortAvatar person={mentor} size="panel" tone="mentor" />
        <div className="min-w-0 flex-1">
          <SheetTitle className="break-words font-['Neue_Montreal','Inter',sans-serif] text-[23px] font-normal leading-tight tracking-[-0.01em]">{mentor.name}</SheetTitle>
          <p className="mt-[3px] text-sm text-[var(--cohort-orange-ink)]">Your mentor</p>
        </div>
      </div>

      <div className="border-t border-[var(--cohort-rule-soft)] px-5 py-4">
        <p className="text-xs uppercase tracking-[0.06em] text-[var(--cohort-ink-muted)]">Next 1:1</p>
        {nextMeeting ? (
          <div className="mt-2 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium">{formatMeetingDateTime(nextMeeting, false, viewerTimeZone)}</p>
              <p className={`mt-0.5 text-sm ${live ? "text-[var(--cohort-orange-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>{live ? "In progress" : "Scheduled"} · {meetingPlace(nextMeeting)}</p>
            </div>
            {canJoin ? (
              <Button type="button" size="auto" onClick={() => onJoinMeeting?.(nextMeeting)} className="cohort-primary-button shrink-0 px-[18px]">Join</Button>
            ) : addToCalendarHref ? (
              <a href={addToCalendarHref} target="_blank" rel="noreferrer" className="cohort-secondary-button inline-flex min-h-11 shrink-0 items-center rounded-xl px-3 text-sm">Add to calendar</a>
            ) : null}
          </div>
        ) : (
          <p className="mt-1.5 text-base font-medium">No 1:1 scheduled yet</p>
        )}
        {lastMeeting ? <p className="mt-2.5 text-sm text-[var(--cohort-ink-muted)]">Last 1:1 scheduled {formatMeetingDateTime(lastMeeting, true, viewerTimeZone)}</p> : null}
      </div>

      {continuityCue && onOpenContinuity ? <button type="button" onClick={() => onOpenContinuity(mentor)} className="cohort-row-action flex min-h-14 w-full items-center justify-between gap-3 border-t border-[var(--cohort-rule-soft)] px-5 py-3 text-left text-sm text-[var(--cohort-forest)]"><span>{continuityCue}</span><ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" /></button> : null}

      <div className="flex flex-col gap-2.5 border-t border-[var(--cohort-rule-soft)] px-5 py-4">
        <MentorActions mentor={mentor} onViewProfile={onViewProfile} />
        <button type="button" onClick={() => onViewProfile?.(mentor)} className="cohort-text-action inline-flex min-h-11 items-center text-left text-[15px]">View full profile</button>
      </div>

      <button type="button" onClick={() => onReportConcern?.(mentor)} className="cohort-row-action flex min-h-[58px] w-full items-center gap-2.5 border-t border-[var(--cohort-rule-soft)] bg-[var(--cohort-paper)] px-5 py-3.5 text-left">
        <Icon name="caution" className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-sm text-[var(--cohort-ink-muted)]">Report a concern privately</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-[var(--cohort-ink-subtle)]" aria-hidden="true" />
      </button>
    </>
  )
}

export function getRelationshipFact(next: RelationshipMeeting | null, last: RelationshipMeeting | null, viewerTimeZone?: string) {
  if (next && isCohortEventLive(next)) return "1:1 in progress now"
  if (next) return `1:1 ${formatMeetingDateTime(next, true, viewerTimeZone)}`
  if (last?.startTimeIso) {
    const date = new Date(last.startTimeIso)
    if (!Number.isNaN(date.getTime())) return `Last 1:1 scheduled ${formatMeetingDateTime(last, true, viewerTimeZone)}`
  }
  return "No meeting history yet"
}

function MenteeList({
  viewerTimeZone,
  mentees,
  events,
  cohortId,
  currentUserId,
  onScheduleTime,
  onJoinMeeting,
  onOpenPeople,
  onViewProfile,
  continuityCues = {},
  onOpenContinuity,
  sheetTitle = false,
}: {
  viewerTimeZone?: string
  mentees: CohortRelationshipPerson[]
  events: RelationshipMeeting[]
  cohortId?: string
  currentUserId?: string
  onScheduleTime?: (person: CohortRelationshipPerson) => void
  onJoinMeeting?: (event: EventsListItem) => void
  onOpenPeople?: () => void
  onViewProfile?: (person: CohortRelationshipPerson) => void
  continuityCues?: Record<string, string>
  onOpenContinuity?: (person: CohortRelationshipPerson) => void
  sheetTitle?: boolean
}) {
  const rows = useMemo(() => mentees.map((person) => {
    const { next, last } = getRelationshipMeetings(events, cohortId, currentUserId, person.id)
    return { person, next, last, live: Boolean(next && isCohortEventLive(next)) }
  }), [cohortId, currentUserId, events, mentees])
  const visibleRows = rows.slice(0, 4)
  const emailAddresses = mentees.map((person) => person.email).filter((email): email is string => Boolean(email))
  const messageAllHref = emailAddresses.length > 0 ? `mailto:?bcc=${encodeURIComponent(emailAddresses.join(","))}` : null

  return (
    <>
      <div className="flex items-baseline justify-between gap-3 px-5 pb-3.5 pt-[18px]">
        {sheetTitle ? (
          <SheetTitle className="font-['Neue_Montreal','Inter',sans-serif] text-[21px] font-normal tracking-[-0.01em]">Your mentees</SheetTitle>
        ) : (
          <h2 className="font-['Neue_Montreal','Inter',sans-serif] text-[21px] font-normal tracking-[-0.01em]">Your mentees</h2>
        )}
        <span className="text-sm text-[var(--cohort-ink-muted)]">{mentees.length}</span>
      </div>
      <div className="border-t border-[var(--cohort-rule-soft)]">
        {visibleRows.length > 0 ? visibleRows.map(({ person, next, last, live }) => (
          <div key={person.id} className="flex min-h-[64px] items-center gap-3 border-b border-[var(--cohort-rule-soft)] px-5 py-3">
            <button type="button" onClick={() => continuityCues[String(person.id)] && onOpenContinuity ? onOpenContinuity(person) : onViewProfile?.(person)} className="cohort-mentee-row cohort-row-action -my-1.5 -ml-2 flex min-w-0 flex-1 items-center gap-3 rounded-xl px-2 py-1.5 text-left">
              <CohortAvatar person={person} size="row" />
              <span className="min-w-0 flex-1">
                <span className="block break-words text-base font-medium">{person.name}</span>
                <span className={`mt-0.5 block break-words text-sm ${live ? "text-[var(--cohort-orange-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>{getRelationshipFact(next, last, viewerTimeZone)}</span>
                {continuityCues[String(person.id)] ? <span className="mt-1 block text-xs text-[var(--cohort-forest)]">{continuityCues[String(person.id)]}</span> : null}
              </span>
              {!live ? <ChevronRight className="h-4 w-4 shrink-0 text-[var(--cohort-ink-subtle)]" aria-hidden="true" /> : null}
            </button>
            {live && next?.canJoinVideo && onJoinMeeting ? <Button type="button" size="auto" onClick={() => onJoinMeeting(next)} className="cohort-primary-button min-h-10 shrink-0 rounded-[10px] px-3.5 text-sm">Join</Button> : null}
            {!next && onScheduleTime ? <Button type="button" size="auto" variant="outline" aria-label={`Schedule 1:1 with ${person.name}`} onClick={() => onScheduleTime(person)} className="cohort-secondary-button min-h-10 shrink-0 rounded-[10px] px-3.5 text-sm">Schedule</Button> : null}
          </div>
        )) : <p className="px-5 py-5 text-sm text-[var(--cohort-ink-muted)]">No mentees are assigned yet.</p>}
      </div>
      <div className="flex min-h-[72px] items-center justify-between gap-3 border-t border-[var(--cohort-rule-soft)] bg-[var(--cohort-paper)] px-5 py-3.5">
        <button type="button" onClick={onOpenPeople} className="cohort-text-action min-h-11 text-[15px]">See all {mentees.length}</button>
        {messageAllHref ? <a href={messageAllHref} className="cohort-secondary-button inline-flex min-h-11 items-center rounded-xl px-[18px] text-[15px]">Message all</a> : null}
      </div>
    </>
  )
}

export function CohortRelationshipSpotlight({
  viewerTimeZone,
  display = "responsive",
  viewerIsMentor,
  cohortId,
  currentUserId,
  mentor = null,
  mentees = [],
  events,
  mentorLabel = "mentor",
  menteeLabelPlural = "mentees",
  mobileTrigger,
  onScheduleTime,
  onJoinMeeting,
  onOpenPeople,
  onReportConcern,
  onViewProfile,
  continuityCues = {},
  onOpenContinuity,
}: CohortRelationshipSpotlightProps) {
  const [open, setOpen] = useState(false)
  const { next: mentorNextMeeting, last: mentorLastMeeting } = useMemo(
    () => mentor ? getRelationshipMeetings(events, cohortId, currentUserId, mentor.id) : { next: null, last: null },
    [cohortId, currentUserId, events, mentor],
  )
  const configuredMentorLabel = mentorLabel.toLowerCase()
  const configuredMenteeLabel = menteeLabelPlural.toLowerCase()
  const showMobile = display !== "desktop"
  const showDesktop = display !== "mobile"

  const closeThen = (action?: () => void) => {
    setOpen(false)
    if (action) window.setTimeout(action, 0)
  }

  if (!viewerIsMentor && !mentor) {
    return (
      <>
        {showMobile ? <div className="cohort-relationship-mobile-row -mx-5 flex min-h-14 items-center gap-3 border-b border-[var(--cohort-rule)] bg-[var(--cohort-rest)] px-5 lg:hidden">
          <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-dashed border-[var(--cohort-rule-strong)] text-sm text-[var(--cohort-ink-muted)]">—</span>
          <span className="min-w-0 flex-1"><span className="block text-xs text-[var(--cohort-ink-muted)]">Your {configuredMentorLabel}</span><span className="block text-[15px] font-medium">Pending match</span></span>
        </div> : null}
        {showDesktop ? <section className="hidden rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-5 lg:block" aria-label={`${mentorLabel} relationship`}>
          <h2 className="text-[21px]">No {configuredMentorLabel} yet</h2>
          <p className="mt-2 text-[15px] leading-6 text-[var(--cohort-ink-muted)]">Your program team is matching you now. You will see them here as soon as they are assigned.</p>
        </section> : null}
      </>
    )
  }

  const mobileSummary = viewerIsMentor
    ? `${mentees.length} ${configuredMenteeLabel}`
    : `${mentor?.name} · ${mentorNextMeeting ? `next 1:1 ${formatMeetingDateTime(mentorNextMeeting, true, viewerTimeZone)}` : "no 1:1 scheduled yet"}`

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {showMobile ? <SheetTrigger asChild onClick={(event) => {
        if (!viewerIsMentor && mentor && continuityCues[String(mentor.id)] && onOpenContinuity) {
          event.preventDefault()
          onOpenContinuity(mentor)
        }
      }}>
        {mobileTrigger || <button type="button" aria-expanded={open} aria-haspopup="dialog" className="cohort-relationship-mobile-row -mx-5 flex min-h-14 w-[calc(100%+2.5rem)] items-center gap-[11px] border-b border-[var(--cohort-rule)] bg-[var(--cohort-rest)] px-5 text-left lg:hidden">
          {viewerIsMentor ? (
            <span className="flex w-[44px] shrink-0 -space-x-2">
              {mentees.slice(0, 2).map((person) => <CohortAvatar key={person.id} person={person} size="mobile" />)}
            </span>
          ) : mentor ? <CohortAvatar person={mentor} size="mobile" tone="mentor" /> : null}
          <span className="min-w-0 flex-1">
            <span className="block text-xs text-[var(--cohort-ink-muted)]">Your {viewerIsMentor ? configuredMenteeLabel : configuredMentorLabel}</span>
            <span className="block break-words text-[15px] font-medium leading-5 text-[var(--cohort-ink)]">{mobileSummary}</span>
            {!viewerIsMentor && mentor && continuityCues[String(mentor.id)] ? <span className="mt-1 block text-xs text-[var(--cohort-forest)]">{continuityCues[String(mentor.id)]}</span> : null}
          </span>
          <ChevronRight className="h-[17px] w-[17px] shrink-0 text-[var(--cohort-ink-subtle)]" aria-hidden="true" />
        </button>}
      </SheetTrigger> : null}

      {showDesktop && viewerIsMentor ? (
        <section className="hidden overflow-hidden rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] lg:block" aria-label={`${menteeLabelPlural} relationships`}>
          <MenteeList viewerTimeZone={viewerTimeZone} mentees={mentees} events={events} cohortId={cohortId} currentUserId={currentUserId} onScheduleTime={onScheduleTime} onJoinMeeting={onJoinMeeting} onOpenPeople={onOpenPeople} onViewProfile={onViewProfile} continuityCues={continuityCues} onOpenContinuity={onOpenContinuity} />
        </section>
      ) : showDesktop && mentor ? (
        <section className="hidden rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-5 lg:block" aria-label={`${mentorLabel} relationship`}>
          <MentorIdentity mentor={mentor} eyebrow={`Your ${configuredMentorLabel}`} />
          <p className="mt-3 text-sm leading-[1.45] text-[var(--cohort-ink-muted)]">{mentorNextMeeting ? `Next 1:1 ${formatMeetingDateTime(mentorNextMeeting, true, viewerTimeZone)}` : "No 1:1 scheduled yet"}</p>
          {continuityCues[String(mentor.id)] && onOpenContinuity ? <button type="button" onClick={() => onOpenContinuity(mentor)} className="cohort-row-action mt-3 flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-[var(--cohort-rule-soft)] px-3 py-2 text-left text-sm text-[var(--cohort-forest)]"><span>{continuityCues[String(mentor.id)]}</span><ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" /></button> : null}
          <div className="mt-3.5 flex gap-2.5">
            {mailtoHref(mentor) ? <a href={mailtoHref(mentor) || undefined} className="cohort-primary-button inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-3 text-[15px] font-semibold">Message</a> : <button type="button" onClick={() => onViewProfile?.(mentor)} className="cohort-primary-button min-h-11 flex-1 px-3 text-[15px] font-semibold">View profile</button>}
            {mailtoHref(mentor, "Schedule a 1:1") ? <a href={mailtoHref(mentor, "Schedule a 1:1") || undefined} className="cohort-secondary-button inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-3 text-[15px]">Ask to schedule</a> : null}
          </div>
        </section>
      ) : null}

      <SheetContent
        side="bottom"
        overlayClassName="cohort-relationship-overlay"
        style={{ pointerEvents: "auto" }}
        className="cohort-experience pointer-events-auto max-h-[calc(100dvh-3rem)] gap-0 overflow-y-auto rounded-t-[22px] border-0 bg-[var(--cohort-rest)] p-0 text-[var(--cohort-ink)] shadow-[0_-22px_50px_-22px_rgba(23,26,34,0.5)] [&>button]:hidden lg:inset-x-auto lg:bottom-auto lg:right-8 lg:top-24 lg:h-auto lg:max-h-[calc(100dvh-7rem)] lg:w-[380px] lg:rounded-2xl lg:border lg:border-[var(--cohort-rule)] lg:shadow-[0_24px_54px_-18px_rgba(23,26,34,0.42)] xl:right-[calc((100vw-72rem)/2)]"
      >
        <SheetDescription className="sr-only">Relationship details and actions.</SheetDescription>
        <div className="flex justify-center pb-0.5 pt-2.5 lg:hidden" aria-hidden="true"><span className="h-1 w-11 rounded-full bg-[var(--cohort-rule-strong)]" /></div>
        {viewerIsMentor ? (
          <MenteeList
            viewerTimeZone={viewerTimeZone}
            mentees={mentees}
            events={events}
            cohortId={cohortId}
            currentUserId={currentUserId}
            onScheduleTime={onScheduleTime ? (person) => closeThen(() => onScheduleTime(person)) : undefined}
            onJoinMeeting={(event) => closeThen(() => onJoinMeeting?.(event))}
            onOpenPeople={() => closeThen(onOpenPeople)}
            onViewProfile={(person) => closeThen(() => onViewProfile?.(person))}
            continuityCues={continuityCues}
            onOpenContinuity={(person) => closeThen(() => onOpenContinuity?.(person))}
            sheetTitle
          />
        ) : mentor ? (
          <MentorPanel
            viewerTimeZone={viewerTimeZone}
            mentor={mentor}
            nextMeeting={mentorNextMeeting}
            lastMeeting={mentorLastMeeting}
            onJoinMeeting={(event) => closeThen(() => onJoinMeeting?.(event))}
            onReportConcern={(person) => closeThen(() => onReportConcern?.(person))}
            onViewProfile={(person) => closeThen(() => onViewProfile?.(person))}
            continuityCue={continuityCues[String(mentor.id)]}
            onOpenContinuity={(person) => closeThen(() => onOpenContinuity?.(person))}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
